import { ResponderMapProvider, ScheduleMapView } from 'features/Responder';
import { PersonnelProvider } from 'features/Responder/features/Personnel';
import { useResponderMap } from 'features/Responder/features/ResponderMap';
import {
  SchedulesProvider,
  useSchedules,
} from 'features/Responder/features/Schedules/contexts/SchedulesContext';
import { AssignmentsProvider } from 'features/Responder/features/Schedules/features/Assignments/contexts/AssignmentsContext';
import ScheduleBuilderComponent from 'features/Responder/features/Schedules/features/ScheduleBuilder';
import ScheduleSelectorPanel from 'features/Responder/features/Schedules/features/ScheduleSelectorPanel';
import ScheduleTelemetry from 'features/Responder/features/Schedules/features/ScheduleTelemetry';
import { useScheduleHydration } from 'features/Responder/features/Schedules/hooks/useScheduleHydration';
import {
  resolveScheduleTour,
  resolveScheduleZoneIds,
} from 'features/Responder/features/Schedules/utils';
import { TourProvider, useTours } from 'features/Responder/features/Tours';
import { useZones, ZonesProvider } from 'features/Responder/features/Zones';
import useResponderDialog from 'features/Responder/hooks/useResponderDialog';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Group, Panel } from 'react-resizable-panels';
import { useLocation, useNavigate } from 'react-router-dom';
import { ResizablePanelHandle } from 'utils/panelResizeUtils';

const usePageHeight = () => {
  const pageRef = useRef(null);
  const [pageHeight, setPageHeight] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const updateHeight = () => {
      if (!pageRef.current) return;
      const topOffset = pageRef.current.getBoundingClientRect().top || 0;
      const nextHeight = Math.max(window.innerHeight - topOffset, 320);
      setPageHeight(nextHeight);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  return { pageRef, pageHeight };
};

const toIdKey = (values = []) =>
  Array.from(new Set((Array.isArray(values) ? values : []).map((value) => `${value}`)))
    .sort()
    .join('|');

const ReadSchedulesLayout = ({
  onCreateSchedule,
  onDeleteSelectedSchedule,
  onEditSelectedSchedule,
  onRefreshRequest,
}) => {
  return (
    <Row className="flex-grow-1" style={{ minHeight: 0 }}>
      <Col className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
        <Group
          orientation="horizontal"
          className="d-flex w-100 flex-grow-1 h-100"
          style={{ minHeight: 0, height: '100%' }}
        >
          <Panel defaultSize={70} minSize={40} className="d-flex flex-column h-100">
            <div className="card border-0 shadow-lg bg-dark text-white h-100">
              <div className="card-body rounded overflow-hidden p-2 h-100">
                <div className="w-100 h-100 rounded-top overflow-hidden">
                  <ScheduleSelectorPanel
                    onCreateSchedule={onCreateSchedule}
                    onDeleteSelectedSchedule={onDeleteSelectedSchedule}
                    onEditSelectedSchedule={onEditSelectedSchedule}
                    onRefreshRequest={onRefreshRequest}
                  />
                </div>
              </div>
            </div>
          </Panel>

          <ResizablePanelHandle direction="vertical" />

          <Panel defaultSize={30} minSize={20} className="d-flex flex-column h-100">
            <Group
              orientation="vertical"
              className="d-flex flex-column h-100"
              style={{ minHeight: 0 }}
            >
              <Panel defaultSize={50} minSize={25} className="d-flex flex-column h-100">
                <div className="card border-0 shadow-lg bg-dark text-white h-100">
                  <div className="card-body h-100 d-flex flex-column p-2" style={{ minHeight: 0 }}>
                    <ScheduleTelemetry scrollHeight="100%" showHistoryTab={false} />
                  </div>
                </div>
              </Panel>
              <ResizablePanelHandle direction="horizontal" />
              <Panel defaultSize={50} minSize={25} className="d-flex flex-column h-100">
                <div className="card border-0 shadow-lg bg-dark text-white h-100">
                  <div className="card-body rounded overflow-hidden p-0 h-100">
                    <ScheduleMapView />
                  </div>
                </div>
              </Panel>
            </Group>
          </Panel>
        </Group>
      </Col>
    </Row>
  );
};

const EditSchedulesLayout = ({
  builderKey,
  initialPrefill,
  onCancelEdit,
  onPersistSuccess,
  onUnsavedChangesChange,
  onRefreshRequest,
}) => {
  return (
    <Row className="flex-grow-1" style={{ minHeight: 0 }}>
      <Col className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
        <Group
          orientation="horizontal"
          className="d-flex w-100 flex-grow-1 h-100"
          style={{ minHeight: 0, height: '100%' }}
        >
          <Panel defaultSize={70} minSize={40} className="d-flex flex-column h-100">
            <Group
              orientation="vertical"
              className="d-flex flex-column h-100"
              style={{ minHeight: 0 }}
            >
              <Panel defaultSize={65} minSize={35} className="d-flex flex-column h-100">
                <div className="card border-0 shadow-lg bg-dark text-white h-100">
                  <div className="card-body rounded overflow-hidden p-0 h-100">
                    <ScheduleBuilderComponent
                      key={builderKey}
                      initialPrefill={initialPrefill}
                      onCancelEdit={onCancelEdit}
                      onPersistSuccess={onPersistSuccess}
                      onUnsavedChangesChange={onUnsavedChangesChange}
                    />
                  </div>
                </div>
              </Panel>

              <ResizablePanelHandle direction="horizontal" />

              <Panel defaultSize={35} minSize={25} className="d-flex flex-column h-100">
                <Group
                  orientation="horizontal"
                  className="d-flex flex-row h-100"
                  style={{ minHeight: 0 }}
                >
                  <Panel defaultSize={50} minSize={25} className="d-flex flex-column h-100">
                    <div className="card border-0 shadow-lg bg-dark text-white h-100">
                      <div
                        className="card-body h-100 d-flex flex-column p-2"
                        style={{ minHeight: 0 }}
                      >
                        <ScheduleTelemetry scrollHeight="100%" showHistoryTab={false} />
                      </div>
                    </div>
                  </Panel>
                  <ResizablePanelHandle direction="vertical" />
                  <Panel defaultSize={50} minSize={25} className="d-flex flex-column h-100">
                    <div className="card border-0 shadow-lg bg-dark text-white h-100">
                      <div className="card-body rounded overflow-hidden p-0 h-100">
                        <ScheduleMapView />
                      </div>
                    </div>
                  </Panel>
                </Group>
              </Panel>
            </Group>
          </Panel>

          <ResizablePanelHandle direction="vertical" />

          <Panel defaultSize={32} minSize={20} className="d-flex flex-column h-100">
            <ScheduleSelectorPanel onRefreshRequest={onRefreshRequest} />
          </Panel>
        </Group>
      </Col>
    </Row>
  );
};

const SchedulesLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { pageRef, pageHeight } = usePageHeight();
  const { activeEditSchedule, deleteSchedule, setActiveEditSchedule, fetchSchedules } =
    useSchedules();
  const { confirm: showConfirm, dialogElement } = useResponderDialog();
  const { tours, activeTourData, selectTour } = useTours();
  const { activeZoneId, setActiveZoneId, setSelectedZoneIds: setZoneSelectedZoneIds } = useZones();
  const {
    selectedTourIds,
    setSelectedTourIds,
    selectedZoneIds,
    setSelectedZoneIds: setMapSelectedZoneIds,
  } = useResponderMap();
  const [scheduleEditing, setScheduleEditing] = useState(false);
  const [hasUnsavedScheduleChanges, setHasUnsavedScheduleChanges] = useState(false);
  const [builderKey, setBuilderKey] = useState(0);
  const [initialPrefill, setInitialPrefill] = useState(null);
  const lastHydrationKeyRef = useRef(null);
  const editHydrationKeyRef = useRef(null);
  const wasEditingRef = useRef(false);
  const consumedPrefillKeyRef = useRef(null);

  useScheduleHydration();

  useEffect(() => {
    // Schedules page defaults to read mode on load.
    setScheduleEditing(false);
  }, []);

  const handleCreateSchedule = useCallback(() => {
    setActiveEditSchedule?.(null);
    setInitialPrefill(null);
    setBuilderKey((prev) => prev + 1);
    setScheduleEditing(true);
  }, [setActiveEditSchedule]);

  const handleEditSelectedSchedule = useCallback(() => {
    if (!activeEditSchedule) return;
    setInitialPrefill(null);
    setScheduleEditing(true);
  }, [activeEditSchedule]);

  const handleDeleteSelectedSchedule = useCallback(async () => {
    if (!activeEditSchedule?.id) return;
    const shouldDelete = await showConfirm({
      title: 'Delete schedule',
      message: 'Delete this schedule? This cannot be undone.',
      confirmLabel: 'Delete schedule',
      cancelLabel: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!shouldDelete) return;
    await deleteSchedule?.(activeEditSchedule.id);
    setActiveEditSchedule?.(null);
  }, [activeEditSchedule?.id, deleteSchedule, setActiveEditSchedule, showConfirm]);

  const handleCancelEdit = useCallback(() => {
    setScheduleEditing(false);
    setHasUnsavedScheduleChanges(false);
    setInitialPrefill(null);
  }, []);

  const handlePersistSuccess = useCallback(() => {
    setScheduleEditing(false);
    setHasUnsavedScheduleChanges(false);
    setInitialPrefill(null);
  }, []);

  const handleUnsavedChangesChange = useCallback((nextValue) => {
    setHasUnsavedScheduleChanges(!!nextValue);
  }, []);

  const handleRefreshRequest = useCallback(async () => {
    if (scheduleEditing && hasUnsavedScheduleChanges) {
      const shouldDiscard = await showConfirm({
        title: 'Discard schedule changes',
        message:
          'You have unsaved changes. Refreshing schedules will discard these changes. Continue?',
        confirmLabel: 'Discard changes',
        cancelLabel: 'Keep editing',
      });
      if (!shouldDiscard) return;
      setHasUnsavedScheduleChanges(false);
    }
    fetchSchedules?.();
  }, [fetchSchedules, hasUnsavedScheduleChanges, scheduleEditing, showConfirm]);

  useEffect(() => {
    const prefill = location?.state?.responderSchedulePrefill;
    if (!prefill || prefill.entityId == null) return;

    const prefillKey = `${prefill.entityType || ''}:${prefill.entityId}:${prefill.requestedAt || ''}`;
    if (consumedPrefillKeyRef.current === prefillKey) return;
    consumedPrefillKeyRef.current = prefillKey;

    setActiveEditSchedule?.(null);
    setInitialPrefill(prefill);
    setBuilderKey((prev) => prev + 1);
    setScheduleEditing(true);
    setHasUnsavedScheduleChanges(false);

    const stateWithoutPrefill = { ...(location.state || {}) };
    delete stateWithoutPrefill.responderSchedulePrefill;
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: Object.keys(stateWithoutPrefill).length ? stateWithoutPrefill : null,
    });
  }, [
    location.pathname,
    location.search,
    location.state,
    navigate,
    setActiveEditSchedule,
  ]);

  useEffect(() => {
    const wasEditing = wasEditingRef.current;
    const enteringEdit = scheduleEditing && !wasEditing;
    const modeChanged = scheduleEditing !== wasEditing;

    // Always reset the hydration marker on mode switch so read mode can re-apply state.
    if (modeChanged) {
      lastHydrationKeyRef.current = null;
    }

    if (enteringEdit) {
      // Read-mode map hydration must not leak into create/edit mode.
      selectTour(null);
      setSelectedTourIds([]);
      setActiveZoneId(null);
      setZoneSelectedZoneIds([]);
      setMapSelectedZoneIds([]);
    }

    wasEditingRef.current = scheduleEditing;
  }, [
    scheduleEditing,
    selectTour,
    setSelectedTourIds,
    setActiveZoneId,
    setZoneSelectedZoneIds,
    setMapSelectedZoneIds,
  ]);

  useEffect(() => {
    if (!scheduleEditing) {
      editHydrationKeyRef.current = null;
      return;
    }
    if (!activeEditSchedule) {
      editHydrationKeyRef.current = 'create';
      return;
    }

    const scheduleZoneIds = resolveScheduleZoneIds(activeEditSchedule);
    const scheduleZoneKey = toIdKey(scheduleZoneIds);
    const scheduleTour = resolveScheduleTour(activeEditSchedule, tours);
    const hydrationKey = `${activeEditSchedule?.id ?? 'schedule'}|tour:${scheduleTour?.id ?? ''}|zones:${scheduleZoneKey}`;
    if (editHydrationKeyRef.current === hydrationKey) return;
    editHydrationKeyRef.current = hydrationKey;

    const nextTourIds = scheduleTour?.id != null ? [scheduleTour.id] : [];
    if (scheduleTour) {
      selectTour(scheduleTour);
    } else {
      selectTour(null);
    }
    if (toIdKey(selectedTourIds) !== toIdKey(nextTourIds)) {
      setSelectedTourIds(nextTourIds);
    }

    const isZoneSchedule = `${activeEditSchedule?.entity?.type ?? ''}`.toLowerCase() === 'zone';
    if (isZoneSchedule) {
      const nextActiveZoneId = scheduleZoneIds[0] ?? null;
      if (`${activeZoneId ?? ''}` !== `${nextActiveZoneId ?? ''}`) {
        setActiveZoneId(nextActiveZoneId);
      }
      if (toIdKey(selectedZoneIds) !== toIdKey(scheduleZoneIds)) {
        setZoneSelectedZoneIds(scheduleZoneIds);
        setMapSelectedZoneIds(scheduleZoneIds);
      }
    } else if (activeZoneId != null || selectedZoneIds.length) {
      setActiveZoneId(null);
      setZoneSelectedZoneIds([]);
      setMapSelectedZoneIds([]);
    }
  }, [
    activeEditSchedule,
    activeZoneId,
    scheduleEditing,
    selectTour,
    selectedTourIds,
    selectedZoneIds,
    setActiveZoneId,
    setMapSelectedZoneIds,
    setSelectedTourIds,
    setZoneSelectedZoneIds,
    tours,
  ]);

  useEffect(() => {
    if (scheduleEditing) return;
    const scheduleZoneIds = resolveScheduleZoneIds(activeEditSchedule);
    const scheduleTour = resolveScheduleTour(activeEditSchedule, tours);
    const scheduleZoneKey = toIdKey(scheduleZoneIds);
    const hydrationKey = `${activeEditSchedule?.id ?? 'none'}|tour:${scheduleTour?.id ?? ''}|zones:${scheduleZoneKey}`;
    if (lastHydrationKeyRef.current === hydrationKey) return;
    lastHydrationKeyRef.current = hydrationKey;

    if (!activeEditSchedule) {
      if (
        activeTourData ||
        selectedTourIds.length ||
        activeZoneId != null ||
        selectedZoneIds.length
      ) {
        selectTour(null);
        setSelectedTourIds([]);
        setActiveZoneId(null);
        setZoneSelectedZoneIds([]);
        setMapSelectedZoneIds([]);
      }
      return;
    }

    if (scheduleTour) {
      selectTour(scheduleTour);
      const nextTourIds = [scheduleTour.id];
      if (toIdKey(selectedTourIds) !== toIdKey(nextTourIds)) {
        setSelectedTourIds(nextTourIds);
      }
    } else {
      if (activeTourData) selectTour(null);
      if (selectedTourIds.length) setSelectedTourIds([]);
    }

    const isZoneSchedule = `${activeEditSchedule?.entity?.type ?? ''}`.toLowerCase() === 'zone';
    if (isZoneSchedule) {
      const nextZoneIds = scheduleZoneIds;
      const nextActiveZoneId = nextZoneIds[0] ?? null;
      if (`${activeZoneId ?? ''}` !== `${nextActiveZoneId ?? ''}`) {
        setActiveZoneId(nextActiveZoneId);
      }
      if (toIdKey(selectedZoneIds) !== toIdKey(nextZoneIds)) {
        setZoneSelectedZoneIds(nextZoneIds);
        setMapSelectedZoneIds(nextZoneIds);
      }
    } else if (activeZoneId != null || selectedZoneIds.length) {
      setActiveZoneId(null);
      setZoneSelectedZoneIds([]);
      setMapSelectedZoneIds([]);
    }
  }, [
    activeEditSchedule,
    activeTourData,
    activeZoneId,
    scheduleEditing,
    selectedTourIds,
    selectedZoneIds,
    selectTour,
    setActiveZoneId,
    setMapSelectedZoneIds,
    setSelectedTourIds,
    setZoneSelectedZoneIds,
    tours,
  ]);

  return (
    <div
      ref={pageRef}
      className="tour-builder-page py-4"
      style={{
        minHeight: 0,
        height: pageHeight ? `${pageHeight}px` : '100vh',
        background: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Container fluid className="d-flex flex-column" style={{ flex: 1, minHeight: 0 }}>
        {scheduleEditing ? (
          <EditSchedulesLayout
            builderKey={builderKey}
            initialPrefill={initialPrefill}
            onCancelEdit={handleCancelEdit}
            onPersistSuccess={handlePersistSuccess}
            onUnsavedChangesChange={handleUnsavedChangesChange}
            onRefreshRequest={handleRefreshRequest}
          />
        ) : (
          <ReadSchedulesLayout
            onCreateSchedule={handleCreateSchedule}
            onDeleteSelectedSchedule={handleDeleteSelectedSchedule}
            onEditSelectedSchedule={handleEditSelectedSchedule}
            onRefreshRequest={handleRefreshRequest}
          />
        )}
      </Container>
      {dialogElement}
    </div>
  );
};

export default function Schedules() {
  return (
    <PersonnelProvider>
      <TourProvider>
        <ZonesProvider>
          <ResponderMapProvider>
            <SchedulesProvider>
              <AssignmentsProvider>
                <SchedulesLayout />
              </AssignmentsProvider>
            </SchedulesProvider>
          </ResponderMapProvider>
        </ZonesProvider>
      </TourProvider>
    </PersonnelProvider>
  );
}
